import React, {useCallback, useEffect, useState} from 'react';
import {Navigate, useParams} from "react-router-dom";
import {observer} from "mobx-react-lite";
import {toast} from "react-toastify";
import BaseLayout from "../../layouts/BaseLayout";
import GoBackButton from "../../components/common/GoBackButton";
import CompanyObserver from "../../mobx/company/CompanyObserver";
import FetchService from "../../services/api/FetchService";
import {
    EnumChecklistState,
    IChecklistManagementItemResource,
    IChecklistManagementResource,
} from "../../resources/IChecklistManagement.resource";
import {ErrorToastSettings} from "../../services/toast/toastSettings";
import Loader from "../../components/Loader";
import ChecklistStatusFactory from "../../constants/ChecklistStatusFactory";
import ChecklistItemsTable from "./components/ChecklistItemsTable";
import ChecklistMetaEditor from "./components/ChecklistMetaEditor";
import ApproveChecklistButton from "./components/ApproveChecklistButton";
import ExportChecklistCsvButton from "./components/ExportChecklistCsvButton";
import useHasChecklistManagementAccess from "../../hooks/useHasChecklistManagementAccess";
import ROUTES from "../../routes";
import styles from './styles.module.scss';

const ChecklistManagementDetailsPage: React.FC = () => {
    const {isLoading: isLoadingAccess, hasAccess} = useHasChecklistManagementAccess();
    const {id} = useParams<{ id: string }>();
    const [checklist, setChecklist] = useState<IChecklistManagementResource | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const companyId = CompanyObserver.companyId ?? CompanyObserver.getCompanyId();

    const loadChecklist = useCallback(async () => {
        if (!companyId || !id) return;

        try {
            setIsLoading(true);
            const {data} = await FetchService.getChecklistManagementDetails(companyId, Number(id));
            setChecklist(data);
        } catch (error) {
            toast.error('Не вдалося завантажити чеклист', ErrorToastSettings);
        } finally {
            setIsLoading(false);
        }
    }, [companyId, id]);

    useEffect(() => {
        loadChecklist();
    }, [loadChecklist]);

    const handleItemSaved = (updatedItem: IChecklistManagementItemResource) => {
        setChecklist((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                items: prev.items.map((item) => item.itemUuid === updatedItem.itemUuid ? updatedItem : item),
            };
        });
    };

    const handleItemAdded = (newItem: IChecklistManagementItemResource) => {
        setChecklist((prev) => {
            if (!prev) return prev;
            return {...prev, items: [...prev.items, newItem]};
        });
    };

    const handleItemDeleted = (itemUuid: string) => {
        setChecklist((prev) => {
            if (!prev) return prev;
            return {...prev, items: prev.items.filter((item) => item.itemUuid !== itemUuid)};
        });
    };

    const handleApproved = () => {
        setChecklist((prev) => {
            if (!prev) return prev;
            return {...prev, state: EnumChecklistState.APPROVED};
        });
    };

    const handleMetaSaved = (updated: Pick<IChecklistManagementResource, 'name' | 'description'>) => {
        setChecklist((prev) => {
            if (!prev) return prev;
            return {...prev, ...updated};
        });
    };

    if (!isLoadingAccess && !hasAccess) {
        return <Navigate to={ROUTES.HOME} replace/>;
    }

    if (!checklist) {
        return (
            <>
                {isLoading ? <Loader/> : null}
                <BaseLayout title={'Чеклист'}>
                    <GoBackButton/>
                </BaseLayout>
            </>
        );
    }

    const status = ChecklistStatusFactory(checklist.state);
    const isApproved = checklist.state === EnumChecklistState.APPROVED;

    return (
        <>
            {isLoading ? <Loader/> : null}
            <BaseLayout title={checklist.name}>
                <div className={styles.details__header}>
                    <GoBackButton/>
                    <div className={styles.details__info}>
                        <span
                            className={styles.details__status}
                            style={{backgroundColor: status.color}}
                        >
                            {status.name}
                        </span>
                    </div>
                    <ExportChecklistCsvButton
                        companyId={companyId}
                        checklistId={checklist.id}
                        checklistName={checklist.name}
                    />
                    <ApproveChecklistButton
                        companyId={companyId}
                        checklistId={checklist.id}
                        isApproved={isApproved}
                        onApproved={handleApproved}
                    />
                </div>
                <ChecklistMetaEditor
                    companyId={companyId}
                    checklistId={checklist.id}
                    name={checklist.name}
                    description={checklist.description}
                    onSaved={handleMetaSaved}
                />
                <ChecklistItemsTable
                    items={checklist.items}
                    isApproved={isApproved}
                    companyId={companyId}
                    checklistId={checklist.id}
                    onItemSaved={handleItemSaved}
                    onItemAdded={handleItemAdded}
                    onItemDeleted={handleItemDeleted}
                />
            </BaseLayout>
        </>
    );
};

export default observer(ChecklistManagementDetailsPage);
